/**
 * CollisionHandler - Manages collision detection and resolution for movement
 * 
 * Separates collision logic from movement logic for better code organization.
 */

import { Object3D } from 'three';
import { CollisionDetector } from './CollisionDetection';
import type { CollisionCheckOptions, ColliderConfig, CollisionResult } from './CollisionDetection';

/**
 * CollisionHandler
 * 
 * Handles collision detection and resolution for a target object.
 * Works with TransformMovement to provide automatic collision handling.
 */
export class CollisionHandler {
    private target: Object3D;
    private detector?: CollisionDetector;
    private collisionObjects: Object3D[] = [];
    private options: CollisionCheckOptions;

    constructor(
        target: Object3D,
        colliderConfig?: ColliderConfig,
        collisionOptions?: CollisionCheckOptions
    ) {
        this.target = target;
        // Initialize default options
        this.options = {
            checkGround: true,
            checkWalls: true,
            checkCeiling: false,
            minGroundNormal: 0.7,
            skinWidth: 0.02,
            ...collisionOptions
        };

        if (colliderConfig) {
            this.detector = new CollisionDetector(target, colliderConfig);
        }
    }

    /**
     * Enable collision detection
     */
    enable(colliderConfig?: ColliderConfig, collisionOptions?: CollisionCheckOptions): void {
        if (!this.detector) {
            this.detector = new CollisionDetector(this.target, colliderConfig);
        } else if (colliderConfig) {
            this.detector.updateConfig(colliderConfig);
        }

        if (collisionOptions) {
            this.options = { ...this.options, ...collisionOptions };
        }
    }

    /**
     * Disable collision detection
     */
    disable(): void {
        if (this.detector) {
            this.detector.dispose();
            this.detector = undefined;
        }
    }

    /**
     * Add objects to check collision against
     */
    addCollisionObjects(objects: Object3D | Object3D[]): void {
        const objArray = Array.isArray(objects) ? objects : [objects];
        this.collisionObjects.push(...objArray);
    }

    /**
     * Remove an object from collision checks
     */
    removeCollisionObject(object: Object3D): void {
        const index = this.collisionObjects.indexOf(object);
        if (index !== -1) {
            this.collisionObjects.splice(index, 1);
        }
    }

    /**
     * Clear all collision objects
     */
    clearCollisionObjects(): void {
        this.collisionObjects = [];
    }

    /**
     * Update collision options
     */
    updateOptions(options: CollisionCheckOptions): void {
        this.options = { ...this.options, ...options };
    }

    /**
     * Check and resolve collisions
     * Returns true if grounded
     */
    checkAndResolve(): boolean {
        if (!this.detector || this.collisionObjects.length === 0) {
            return false;
        }

        // Get combined collision correction
        const { correction, hasCollisions } =
            this.detector.getCombinedCorrection(
                this.collisionObjects,
                this.options
            );

        if (hasCollisions) {
            // Apply correction to position
            this.target.position.add(correction);
        }

        // Always check for ground state independently
        // This ensures we detect ground even when hitting walls
        const isGrounded = this.isGrounded();

        return isGrounded;
    }

    /**
     * Check if currently grounded
     */
    isGrounded(): boolean {
        if (!this.detector || this.collisionObjects.length === 0) {
            return false;
        }

        // Save current position
        const originalPos = this.target.position.clone();

        // Test with a small downward offset to detect ground contact
        // Use a smaller offset for more precise ground detection
        this.target.position.y -= 0.05;

        // Check for ground collisions at this test position
        const results = this.detector.checkCollisions(
            this.collisionObjects,
            {
                ...this.options,
                checkGround: true,
                checkWalls: false,
                checkCeiling: false
            }
        );

        // Restore original position
        this.target.position.copy(originalPos);

        // Check if any result is a ground collision
        return results.some((r) => r.type === 'ground' && r.hasCollision);
    }

    /**
     * Get all current collision results
     */
    getCollisions(): CollisionResult[] {
        if (!this.detector || this.collisionObjects.length === 0) {
            return [];
        }

        return this.detector.checkCollisions(this.collisionObjects, this.options);
    }

    /**
     * Get the collision detector instance
     */
    getDetector(): CollisionDetector | undefined {
        return this.detector;
    }

    /**
     * Check if collision detection is enabled
     */
    isEnabled(): boolean {
        return this.detector !== undefined && this.collisionObjects.length > 0;
    }

    /**
     * Get collision objects
     */
    getCollisionObjects(): Object3D[] {
        return this.collisionObjects;
    }

    /**
     * Dispose and cleanup
     */
    dispose(): void {
        this.disable();
        this.collisionObjects = [];
    }
}

